import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { isString } from 'lodash';
import { FC, useCallback, useState } from 'react';
import { useDepartmentColumn } from 'src/assets/static/view_department/useDepartmentColumn';
import GridWrapper from 'src/components/GridWrapper';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import useBoolean from 'src/hooks/useBoolean';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useWindowSize from 'src/hooks/useWindowSize';
import { DepartmentGlCode, DepartmentMembers } from 'src/services/useGetDepartmentApi';
import DepartmentGLCodesTable from './DepartmentGLCodesTable';
import DepartmentMembersTable from './DepartmentMembersTable';
import ViewDepartmentsTableToolbar from './tableToolbar/ViewDepartmentsTableToolbar';
const selectable: GridSelectableSettings = {
  enabled: false,
  drag: false,
  cell: false,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100, 350],
};

const defaultProps = {
  excelFileName: `${'View Department'}.xlsx`,
  tableTextId: 'Departments',
  DataItemKey: 'department',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface ViewDepartmentTableProps {}

const ViewDepartmentTable: FC<ViewDepartmentTableProps> = () => {
  const { height } = useWindowSize();
  const [modalType, setModalType] = useState<'glCodes' | 'members'>();
  const [glCodes, setGLCodes] = useState<DepartmentGlCode[]>([]);
  const [members, setMembers] = useState<DepartmentMembers[]>([]);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState('');

  const {
    value: openActivityModal,
    toggle: toggleActivityModal,
    setTrue: setOpenActivityModal,
  } = useBoolean();
  const {
    isDepartmentLoading,
    departmentsDataState,
    setDepartmentsDataState,
    refreshTable,
    departmentTableData,
    departmentTotalWithFilters,
    setDepartmentTableData,
    excelLoading,
    t,
    setSelectedRows,
    isDepartmentDeleting,
  } = useViewDepartment();

  const handleCellFnClick = useCallback(
    ({ data, type }: { data: any; type: 'glCodes' | 'members' }) => {
      setModalType(type);
      if (type === 'glCodes') {
        setSelectedDepartmentName(data?.departmentName || '');
        setGLCodes(data?.glCodes);
      } else if (type === 'members') {
        setSelectedDepartmentName(data?.departmentName || '');
        setMembers(data?.members);
      }
      setOpenActivityModal();
    },
    [setOpenActivityModal]
  );
  const [latestClickedId, setLatestClickedId] = useState(0);

  const viewDepartmentColumns = useDepartmentColumn();

  const changeDataState = useCallback(
    (e: GridDataStateChangeEvent) => {
      setSelectedRows([]);
      setDepartmentsDataState(e.dataState);
    },
    [setDepartmentsDataState, setSelectedRows]
  );

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setSelectedRows((prev: any) => {
        const prevIds = prev.map((item: any) => item.departmentId);
        if (prevIds.includes(event?.dataItem?.departmentId)) {
          return prev.filter((dept: any) => dept.departmentId !== event?.dataItem?.departmentId);
        } else {
          return [...prev, event.dataItem];
        }
      });
    },
    [setSelectedRows]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;
      if (checked) {
        setSelectedRows(departmentTableData);
      } else {
        setSelectedRows([]);
      }
    },
    [departmentTableData, setSelectedRows]
  );

  const cellClickFns = useCallback(
    () => ({
      latestClickedId,
      setLatestClickedId,
      refreshList: refreshTable,
      handleGLCodesOrMemberClick: handleCellFnClick,
    }),
    [refreshTable, handleCellFnClick, latestClickedId]
  );

  const itemChange = useCallback(
    (event: GridItemChangeEvent) => {
      if (event.field === 'editedName' && isString(event.value)) {
        const { field, value, dataItem } = event;
        setDepartmentTableData((prev) =>
          prev.map((row) => {
            if (row.rowId === dataItem.rowId) {
              return { ...row, [field]: value };
            } else {
              return row;
            }
          })
        );
      }
    },
    [setDepartmentTableData]
  );

  return (
    <main className="mt-2">
      <GridWrapper
        id="viewDepartmentTable"
        columns={viewDepartmentColumns}
        dataSource={departmentTableData}
        dataState={departmentsDataState}
        height={`${height - 110}px`}
        total={departmentTotalWithFilters}
        cellClick={cellClickFns}
        gridToolbarChildren={<ViewDepartmentsTableToolbar />}
        onDataStateChange={changeDataState}
        {...defaultProps}
        loading={isDepartmentLoading || excelLoading || isDepartmentDeleting}
        onHeaderSelectionChange={onHeaderSelectionChange}
        onItemChange={itemChange}
        onSelectionChange={onSelectionChange}
        noRecordText={t('No records to display')}
      />

      <Dialog
        width={'780px'}
        id="modal"
        title={
          modalType === 'glCodes'
            ? `${t('Assigned GL Codes')} - ${selectedDepartmentName}`
            : `${t('Assigned Department Members')} - ${selectedDepartmentName}`
        }
        open={openActivityModal}
        onClose={toggleActivityModal}
      >
        {modalType === 'glCodes' && (
          <DepartmentGLCodesTable
            departmentGLCodes={glCodes}
            selectedDepartmentName={selectedDepartmentName}
          />
        )}
        {modalType === 'members' && (
          <DepartmentMembersTable
            departmentMembers={members}
            selectedDepartmentName={selectedDepartmentName}
          />
        )}
        <DialogActionsBar>
          <div className="flex justify-end">
            <Button onClick={toggleActivityModal} color="info">
              {t('Close')}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </main>
  );
};

export default ViewDepartmentTable;
